import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PersonIcon from '@mui/icons-material/Person';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { SurgicalProcedureCoverage, SurgicalProcedureCoverageSection } from '../../../types';

interface SurgicalProcedureCardProps {
  procedure: SurgicalProcedureCoverage;
  percentageValue?: number | string;
}

const SurgicalProcedureCard = ({ procedure, percentageValue = 0 }: SurgicalProcedureCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-HN', {
      style: 'currency',
      currency: 'HNL',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate total with growth simulation percentage
  const calculateTotalWithGrowth = (baseTotal: number) => {
    const percentage =
      typeof percentageValue === 'string' ? parseFloat(percentageValue) || 0 : percentageValue || 0;
    return baseTotal * (1 + percentage / 100);
  };

  const displayTotal = procedure.total ? calculateTotalWithGrowth(procedure.total) : null;

  // Ensure we have a valid ID
  if (!procedure._id) {
    return null;
  }

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      !target.closest('button') &&
      !target.closest('[role="button"]') &&
      !target.closest('.MuiIconButton-root')
    ) {
      setExpanded(!expanded);
    }
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        mb: 2,
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box display='flex' justifyContent='space-between' alignItems='flex-start' mb={2}>
          <Box flex={1} sx={{ pr: 1 }}>
            <Typography
              variant='h6'
              fontWeight={700}
              color='primary'
              gutterBottom
              sx={{
                mb: 1,
                lineHeight: 1.3,
              }}
            >
              {procedure.procedureName}
            </Typography>
            {procedure.cptCode && (
              <Chip
                label={procedure.cptCode}
                size='small'
                color='primary'
                variant='outlined'
                sx={{
                  fontWeight: 600,
                  borderWidth: 2,
                  mb: 1,
                }}
              />
            )}
          </Box>
          <IconButton
            size='small'
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              setExpanded(!expanded);
            }}
            sx={{
              color: 'primary.main',
              '&:hover': {
                backgroundColor: `${theme.palette.primary.main}15`,
              },
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
          <Typography
            variant='h6'
            fontWeight={700}
            color='primary.main'
            sx={{ fontSize: '1.1rem' }}
          >
            {displayTotal ? formatCurrency(displayTotal) : '-'}
          </Typography>
        </Box>

        <Box display='flex' gap={1} flexWrap='wrap' mb={expanded ? 0 : 0}>
          {procedure.cie10Code && (
            <Chip
              icon={<LocalHospitalIcon sx={{ fontSize: '0.9rem !important' }} />}
              label={`CIE-10: ${procedure.cie10Code}`}
              size='small'
              variant='outlined'
              sx={{
                fontWeight: 500,
                borderColor: theme.palette.info.main,
                color: theme.palette.info.main,
              }}
            />
          )}
          {procedure.surgeon && (
            <Chip
              icon={<PersonIcon sx={{ fontSize: '0.9rem !important' }} />}
              label={procedure.surgeon}
              size='small'
              variant='outlined'
              sx={{
                fontWeight: 500,
                borderColor: theme.palette.secondary.main,
                color: theme.palette.secondary.main,
              }}
            />
          )}
          {procedure.anesthesiaType && (
            <Chip
              label={procedure.anesthesiaType}
              size='small'
              variant='outlined'
              sx={{
                fontWeight: 500,
                borderColor: theme.palette.warning.main,
                color: theme.palette.warning.main,
              }}
            />
          )}
          {procedure.hospitalDays && (
            <Chip
              icon={<CalendarTodayIcon sx={{ fontSize: '0.9rem !important' }} />}
              label={procedure.hospitalDays}
              size='small'
              variant='outlined'
              sx={{
                fontWeight: 500,
                borderColor: theme.palette.success.main,
                color: theme.palette.success.main,
              }}
            />
          )}
          {procedure.surgicalHours && (
            <Chip
              icon={<AccessTimeIcon sx={{ fontSize: '0.9rem !important' }} />}
              label={procedure.surgicalHours}
              size='small'
              variant='outlined'
              sx={{
                fontWeight: 500,
                borderColor: theme.palette.info.main,
                color: theme.palette.info.main,
              }}
            />
          )}
        </Box>

        <Collapse in={expanded} timeout='auto' unmountOnExit>
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: `2px solid ${theme.palette.divider}`,
            }}
          >
            {procedure.description && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: `${theme.palette.primary.main}08`,
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                }}
              >
                <Typography variant='body2' color='text.primary' sx={{ lineHeight: 1.7 }}>
                  {procedure.description}
                </Typography>
              </Box>
            )}

            {procedure.sections && procedure.sections.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant='subtitle1'
                  fontWeight={700}
                  color='primary'
                  gutterBottom
                  sx={{ mb: 2 }}
                >
                  Secciones de Cobertura ({procedure.sections.length})
                </Typography>

                {procedure.sections.map(
                  (section: SurgicalProcedureCoverageSection, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: `${theme.palette.grey[50]}`,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1.5,
                        }}
                      >
                        <Typography variant='body2' fontWeight={700} color='primary'>
                          {section.name}
                        </Typography>
                        {section.subtotal && (
                          <Chip
                            label={formatCurrency(calculateTotalWithGrowth(section.subtotal))}
                            size='small'
                            color='primary'
                            sx={{ fontWeight: 700 }}
                          />
                        )}
                      </Box>

                      {section.items && section.items.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          {section.items.slice(0, 5).map((item, itemIndex) => (
                            <Box
                              key={itemIndex}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                py: 0.75,
                                borderBottom:
                                  itemIndex < Math.min(section.items.length, 5) - 1
                                    ? `1px solid ${theme.palette.divider}`
                                    : 'none',
                              }}
                            >
                              <Typography variant='body2' sx={{ flex: 1, fontSize: '0.85rem' }}>
                                {item.detail}
                              </Typography>
                              <Typography
                                variant='body2'
                                fontWeight={600}
                                color='primary.main'
                                sx={{ ml: 1, fontSize: '0.85rem' }}
                              >
                                {formatCurrency(calculateTotalWithGrowth(item.total))}
                              </Typography>
                            </Box>
                          ))}
                          {section.items.length > 5 && (
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}
                            >
                              +{section.items.length - 5} ítems más...
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )
                )}

                {procedure.total && (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: `2px solid ${theme.palette.primary.main}`,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.main}05 100%)`,
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography variant='h6' fontWeight={800} color='primary.main' align='right'>
                      Total:{' '}
                      {displayTotal
                        ? formatCurrency(displayTotal)
                        : formatCurrency(procedure.total || 0)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default SurgicalProcedureCard;
